import * as React from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';

import { withModal } from 'components/modal/ModalHOC';
import RecentMediaGrid from 'modules/proposal/components/recent-media-grid/RecentMediaGrid';

import ImageTile from './ImageTile';

import {
  POSTS_AMOUNT_INSTAGRAM,
  POSTS_AMOUNT_INSTAGRAM_LEGACY,
  POSTS_AMOUNT_TIKTOK
} from 'modules/proposal/utils/creator-proposal-utils';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import { getTiktokEmbedUrl } from 'utils/channels';

import { useProposalImagePrefetch } from 'modules/proposal/hooks/use-proposal-image-prefetch';

import type { GalleryModalItem, ProposalImage, State } from 'types';
import type { ProposalTileChannelType } from 'modules/proposal/types/ProposalTile.type';

import './proposal-image-grid.scss';

type Props = {
  profileId: number,
  channelId: number,
  channelType: ProposalTileChannelType,
  canSetMedia?: boolean,
  posts: Array<ProposalImage | null>,
  onMediaSet: (image: ProposalImage | null, imageIndex: number, channelType: ProposalTileChannelType) => void
};

const EditModalHOCProps = {
  modalContent: RecentMediaGrid,
  closeIconPosition: 'corner',
  closeIconType: 'fancy',
  closeOnOverlayClick: true
};
const ImageTileWithEditModal = withModal(ImageTile, EditModalHOCProps);

const ImageTileWithViewModal = withModal(ImageTile, WITH_GALLERY_MODAL_CONFIG);

function mapPostsToGalleryModal(posts: Array<ProposalImage | null>, channelType: ProposalTileChannelType): Array<GalleryModalItem> {
  return posts.map((post) => {
    if (!post) {
      return {
        type: 'placeholder',
        assetUrl: ''
      };
    }

    if (channelType === 'tiktok') {
      return {
        type: 'tiktok',
        url: post.url ?? undefined,
        embedUrl: getTiktokEmbedUrl(post.url ?? ''),
        assetUrl: post.assetUrl ?? ''
      };
    }

    return {
      type: channelType,
      url: post.url ?? undefined,
      assetUrl: post.assetUrl ?? ''
    };
  });
}

function getToken(): string {
  const urlParams = new URLSearchParams(window.location.search);

  return urlParams.get('token') || '';
}

const ProposalImageGrid = (
  {
    posts = [],
    onMediaSet,
    profileId,
    channelId,
    channelType,
    canSetMedia
  }: Props
) => {
  const elements: Array<JSX.Element> = [];
  const userConsents = useSelector((state: State) => state.consentReducer.consents);
  const fetchedPosts = useProposalImagePrefetch(posts, channelType, getToken());
  const [lastPickedElement, setLastPickedElement] = React.useState<string | undefined | null>(null);
  const amountOfFilledPosts = posts.filter((post) => Boolean(post && post.url)).length;
  const isLegacyRenderMode = channelType === 'instagram'
    && !canSetMedia
    && amountOfFilledPosts === POSTS_AMOUNT_INSTAGRAM_LEGACY;
  const hasVideoIcon = channelType === 'tiktok';

  let amountOfPostsToRender = POSTS_AMOUNT_INSTAGRAM;

  if (isLegacyRenderMode) {
    amountOfPostsToRender = POSTS_AMOUNT_INSTAGRAM_LEGACY;
  }

  if (channelType === 'tiktok') {
    amountOfPostsToRender = POSTS_AMOUNT_TIKTOK;
  }

  const hasConsent = userConsents[channelType];

  for (let i = 0; i < amountOfPostsToRender; i++) {
    if (canSetMedia) {
      elements.push(
        <ImageTileWithEditModal
          key={`image-${i}-post-${fetchedPosts[i]?.url}`}
          post={fetchedPosts[i]}
          hasConsent={hasConsent}
          channelType={channelType}
          index={i}
          canSetMedia
          withVideoIcon={hasVideoIcon}
          onRemove={() => onMediaSet(null, i, channelType)}
          modalContentProps={{
            profileId,
            channelId,
            isTikTok: channelType === 'tiktok',
            lastPickedElement,
            alreadyPickedPosts: fetchedPosts.filter(_ => _),
            onImageClick: (post: ProposalImage) => {
              setLastPickedElement(post.url);
              onMediaSet(post, i, channelType);
            }
          }}
        />
      );
    } else if (hasConsent) {
      elements.push(
        <ImageTileWithViewModal
          key={`image-${i}-post-${fetchedPosts[i]?.url}`}
          post={fetchedPosts[i]}
          hasConsent={hasConsent}
          channelType={channelType}
          index={i}
          withVideoIcon={hasVideoIcon}
          onRemove={() => {}}
          modalContentProps={{
            items: mapPostsToGalleryModal(fetchedPosts, channelType),
            currentPosition: i
          }}
        />
      );
    } else {
      elements.push(
        <ImageTile
          key={`image-${i}-post-${fetchedPosts[i]?.url}`}
          post={fetchedPosts[i]}
          hasConsent={hasConsent}
          channelType={channelType}
          index={i}
          withVideoIcon={hasVideoIcon}
          onRemove={() => {}}
        />
      );
    }
  }

  const sectionClassNames = classnames('proposal-image-grid-v2', {
    'proposal-image-grid-v2--legacy-view': isLegacyRenderMode,
    'proposal-image-grid-v2--tiktok': channelType === 'tiktok'
  });

  return (
    <section className={sectionClassNames}>
      {elements}
    </section>
  );
};

export default ProposalImageGrid;
